import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { login as apiLogin } from '../services/api'

type AuthContextValue = {
  token: string | null
  isAuthenticated: boolean
  login: (email: string, password: string) => Promise<void>
  setSessionToken: (token: string) => void
  logout: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setToken] = useState<string | null>(() =>
    localStorage.getItem('messengerToken')
  )

  useEffect(() => {
    if (token) localStorage.setItem('messengerToken', token)
    else localStorage.removeItem('messengerToken')
  }, [token])

  const value = useMemo<AuthContextValue>(
    () => ({
      token,
      isAuthenticated: Boolean(token),
      setSessionToken: (t: string) => setToken(t),
      logout: () => setToken(null),
      login: async (email: string, password: string) => {
        const data = await apiLogin(email, password)
        const next = data.accessToken || data.token
        if (!next) throw new Error('Login succeeded but no token returned')
        setToken(next)
      },
    }),
    [token]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
